import React, {useMemo, useState} from 'react'
import {Pressable, ScrollView, StyleSheet, TextInput, View} from 'react-native'

import {useOpenChat} from '#/state/chat-bubble'
import {
  type OnlineUser,
  type PresenceStatus,
  useOnlineUsers,
} from '#/state/chat-bubble/presence'
import {useListConvos} from '#/state/queries/messages/list-conversations'
import {useSession} from '#/state/session'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, useTheme} from '#/alf'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'

function StatusDot({status}: {status: PresenceStatus}) {
  const color =
    status === 'online'
      ? '#22c55e'
      : status === 'idle'
        ? '#eab308'
        : '#9ca3af'

  return (
    <View
      style={[
        styles.statusDot,
        {backgroundColor: color},
      ]}
    />
  )
}

export function OnlineUsersPanel() {
  const t = useTheme()
  const {users, isLoading} = useOnlineUsers()
  const [search, setSearch] = useState('')
  const openChat = useOpenChat()
  const {currentAccount} = useSession()
  const {accepted} = useListConvos()

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users
    const lower = search.toLowerCase()
    return users.filter(
      u =>
        u.displayName.toLowerCase().includes(lower) ||
        u.handle.toLowerCase().includes(lower),
    )
  }, [users, search])

  const handleUserPress = (user: OnlineUser) => {
    // Try to find existing convoId for this user
    let convoId: string | undefined
    if (accepted) {
      for (const convo of accepted) {
        const recipient = convo.members.find(
          m => m.did !== currentAccount?.did,
        )
        if (recipient?.did === user.did) {
          convoId = convo.id
          break
        }
      }
    }

    openChat(
      user.did,
      {
        did: user.did,
        handle: user.handle,
        displayName: user.displayName,
        avatar: user.avatar,
      },
      convoId,
    )
  }

  return (
    <View
      style={[
        styles.panel,
        t.atoms.bg,
        {
          borderColor: t.atoms.border_contrast_low.borderColor,
        },
      ]}>
      <View style={styles.header}>
        <Text style={[a.text_md, a.font_bold, t.atoms.text]}>
          Mutuals
        </Text>
        <Text style={[a.text_xs, {color: t.palette.contrast_500}]}>
          People you follow who follow you back
        </Text>
      </View>

      <View style={[styles.searchContainer]}>
        <TextInput
          value={search}
          onChangeText={setSearch}
          placeholder="Search..."
          placeholderTextColor={t.palette.contrast_500}
          style={[
            styles.searchInput,
            t.atoms.text,
            t.atoms.bg_contrast_25,
            {borderColor: t.atoms.border_contrast_low.borderColor},
          ]}
        />
      </View>

      <ScrollView style={styles.list} showsVerticalScrollIndicator={false}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <Loader size="lg" />
          </View>
        ) : filteredUsers.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={[a.text_sm, {color: t.palette.contrast_500}]}>
              {search ? 'No results' : 'No mutuals online'}
            </Text>
          </View>
        ) : (
          filteredUsers.map(user => (
            <UserRow
              key={user.did}
              user={user}
              onPress={() => handleUserPress(user)}
            />
          ))
        )}
      </ScrollView>
    </View>
  )
}

function UserRow({
  user,
  onPress,
}: {
  user: OnlineUser
  onPress: () => void
}) {
  const t = useTheme()

  return (
    <Pressable
      onPress={onPress}
      style={({hovered}: any) => [
        styles.userRow,
        hovered && {backgroundColor: t.atoms.bg_contrast_25.backgroundColor},
      ]}>
      <View style={styles.avatarContainer}>
        <UserAvatar
          type="user"
          size={36}
          avatar={user.avatar}
        />
        <StatusDot status={user.status} />
      </View>
      <View style={styles.userInfo}>
        <Text
          style={[a.text_sm, a.font_bold, t.atoms.text]}
          numberOfLines={1}>
          {user.displayName}
        </Text>
        <Text
          style={[a.text_xs, {color: t.palette.contrast_500}]}
          numberOfLines={1}>
          @{user.handle}
        </Text>
      </View>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  panel: {
    position: 'absolute',
    bottom: 64,
    right: 0,
    width: 280,
    maxHeight: 400,
    borderRadius: 12,
    borderWidth: 1,
    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  } as any,
  header: {
    paddingHorizontal: 16,
    paddingTop: 14,
    paddingBottom: 8,
  },
  searchContainer: {
    paddingHorizontal: 12,
    paddingBottom: 8,
  },
  searchInput: {
    height: 34,
    borderRadius: 17,
    paddingHorizontal: 14,
    fontSize: 13,
    borderWidth: 1,
  },
  list: {
    flex: 1,
    maxHeight: 300,
  },
  loadingContainer: {
    padding: 24,
    alignItems: 'center' as const,
  },
  emptyContainer: {
    padding: 24,
    alignItems: 'center' as const,
  },
  userRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 10,
    cursor: 'pointer',
  },
  avatarContainer: {
    position: 'relative' as const,
  },
  statusDot: {
    position: 'absolute' as const,
    bottom: 0,
    right: 0,
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: '#fff',
  },
  userInfo: {
    flex: 1,
    gap: 1,
  },
})
